import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { CaretLeftIcon } from "@phosphor-icons/react/dist/csr/CaretLeft";
import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import { CaretUpIcon } from "@phosphor-icons/react/dist/csr/CaretUp";
import { useMemo, useRef, useState, Fragment } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@volca/ui/components/ui/table";
import { cn } from "@volca/ui/lib/utils";
import { useReportCurrency } from "@/profitability/currency";
import { FlagBadge } from "@/profitability/records/judged-cells";
import { isQuiet, rateRule } from "@/profitability/records/judgement";
import {
  ReportMetadata,
  useReportPresentation,
} from "@/profitability/report-presentation";
import { useReviewSpec } from "@/profitability/review/review-spec";
import type {
  ReportDataset,
  ReportRow,
  ReportRowDetail,
} from "@/profitability/types";
import { useRowDetail } from "./row-detail-context";
import { sortRows } from "./sort-rows";
import { isNumericColumn, splitColumns, tableHeight } from "./table-layout";
import { TableRowName } from "./table-row-name";
import { columnLabel, groupParts, groupTableRows } from "./utils";
import { ValueCell } from "./value-cell";
const PAGE_SIZE = 50;
export const ReportTable = ({
  rows,
  fields,
  groups,
  dataset,
  detail,
  headline,
  fill,
  defaultSort,
}: {
  rows: ReportRow[];
  fields: string[];
  groups: string[];
  dataset: ReportDataset;
  detail?: ReportRowDetail;
  headline?: string[];
  /** True when the table has its pane to itself. */
  fill?: boolean;
  defaultSort?: { by: string; direction: "asc" | "desc" };
}) => {
  const classic = useReportPresentation() === "classic";
  const currency = useReportCurrency();
  const review = useReviewSpec();
  // The rate a row is held to, when the report sets a target for it.
  const rule = useMemo(() => rateRule(dataset, review), [dataset, review]);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState(defaultSort);
  const scroller = useRef<HTMLDivElement>(null);
  // A new page starts at its first row, not wherever the last one was left.
  const goToPage = (next: number) => {
    setPage(next);
    scroller.current?.scrollTo({ top: 0 });
  };
  const rowDetail = useRowDetail();
  const sorted = useMemo(
    () =>
      sort
        ? sortRows(
            rows,
            dataset.columns.find((column) => column.key === sort.by),
            sort.direction,
          )
        : rows,
    [rows, sort, dataset.columns],
  );
  const grouped = useMemo(
    () => groupTableRows(sorted, groups),
    [sorted, groups],
  );
  const ordered = useMemo(
    () => grouped.flatMap((group) => group.rows),
    [grouped],
  );
  const visible = groupTableRows(
    ordered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    groups,
  );
  const pages = Math.ceil(rows.length / PAGE_SIZE);
  const columns = fields.map(
    (field) => dataset.columns.find((column) => column.key === field)!,
  );
  // The figure the report sorts by leads; the rest are its workings.
  const { identifier, subtitle, details, extra, money } = splitColumns(
    columns,
    {
      leadKey: defaultSort?.by,
      titleKey: detail?.title,
      subtitleKey: detail?.subtitle,
    },
  );
  const beside = [...extra, ...money];
  const headed = identifier ? [identifier, ...beside] : columns;
  return (
    <Card className="min-h-0 gap-0 overflow-hidden py-0">
      <Stack
        ref={scroller}
        gap={0}
        align="stretch"
        className={cn(
          "overflow-auto *:data-[slot=table-container]:overflow-visible",
          tableHeight(!!fill, pages > 1),
        )}
      >
        <Table>
          <TableHeader className="sticky top-0 bg-surface-raised">
            <TableRow>
              {headed.map((column) => (
                <TableHead
                  key={column.key}
                  aria-sort={
                    sort?.by === column.key
                      ? sort.direction === "asc"
                        ? "ascending"
                        : "descending"
                      : undefined
                  }
                  className={cn("h-8 px-0 whitespace-nowrap", {
                    "text-right": ["number", "currency", "percent"].includes(
                      column.type,
                    ),
                  })}
                >
                  <Button
                    variant="ghost"
                    type="button"
                    onClick={() => {
                      goToPage(0);
                      setSort({
                        by: column.key,
                        direction:
                          sort?.by === column.key && sort.direction === "asc"
                            ? "desc"
                            : "asc",
                      });
                    }}
                    className={cn("h-8 w-full justify-start gap-1 px-3", {
                      "justify-end": ["number", "currency", "percent"].includes(
                        column.type,
                      ),
                      "text-ink": sort?.by === column.key,
                    })}
                  >
                    {columnLabel(column)}
                    {sort?.by === column.key &&
                      (sort.direction === "asc" ? (
                        <CaretUpIcon className="size-3" />
                      ) : (
                        <CaretDownIcon className="size-3" />
                      ))}
                  </Button>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((group) => (
              <Fragment key={group.key}>
                {!!groups.length && (
                  <TableRow>
                    <TableCell
                      colSpan={headed.length}
                      className="bg-surface-sunken px-3 py-2 font-medium whitespace-normal"
                    >
                      <ReportMetadata>
                        {groupParts(group.rows[0], groups, dataset, currency)}
                      </ReportMetadata>
                    </TableCell>
                  </TableRow>
                )}
                {group.rows.map((row, index) => (
                  <TableRow
                    key={index}
                    onClick={
                      rowDetail
                        ? () =>
                            rowDetail.openRow({
                              row,
                              dataset,
                              detail,
                              headline,
                            })
                        : undefined
                    }
                    className={cn({
                      // Only where there is a panel to open it in: a row that
                      // looks clickable and does nothing is worse than a plain
                      // one.
                      "cursor-pointer hover:bg-neutral-subtle-hovered":
                        !!rowDetail && rowDetail.open?.row !== row,
                      "cursor-pointer": !!rowDetail,
                      "bg-selected": rowDetail?.open?.row === row,
                    })}
                  >
                    {identifier && (
                      <TableCell className="w-[34%] min-w-60 px-3 py-3 align-top whitespace-normal">
                        <TableRowName
                          dataset={dataset}
                          row={row}
                          identifier={identifier}
                          subtitle={subtitle}
                          details={details}
                          clickable={!!rowDetail}
                        />
                      </TableCell>
                    )}
                    {(identifier ? beside : columns).map((column) => (
                      <TableCell
                        key={column.key}
                        className={cn("px-3 py-3 align-top whitespace-nowrap", {
                          "text-right font-medium tabular-nums":
                            isNumericColumn(column),
                          "text-subtle tabular-nums":
                            !classic && isQuiet(column),
                          "text-danger":
                            classic &&
                            isNumericColumn(column) &&
                            Number(row[column.key]) < 0,
                        })}
                      >
                        {!classic && column.flag ? (
                          <FlagBadge reason={row[column.key]} />
                        ) : (
                          <ValueCell
                            row={row}
                            column={column}
                            dataset={dataset}
                            currency={currency}
                            rule={rule}
                          />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </Stack>
      <Stack
        gap={2}
        align="center"
        justify="between"
        direction="row"
        className="h-8 w-full flex-none border-t border-line px-3 text-xs text-subtle"
      >
        <span>
          {(page * PAGE_SIZE + 1).toLocaleString()}–
          {Math.min((page + 1) * PAGE_SIZE, rows.length).toLocaleString()} of{" "}
          {rows.length.toLocaleString()}
        </span>
        {pages > 1 && (
          <Stack direction="row" align="center" gap={1}>
            <span>
              Page {page + 1} of {pages}
            </span>
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label="Previous page"
              disabled={!page}
              onClick={() => goToPage(page - 1)}
            >
              <CaretLeftIcon className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label="Next page"
              disabled={page + 1 >= pages}
              onClick={() => goToPage(page + 1)}
            >
              <CaretRightIcon className="size-4" />
            </Button>
          </Stack>
        )}
      </Stack>
    </Card>
  );
};
